import React, { useState } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/contexts/auth/useAuth";
import { cn } from "@/utils/cn";
import { shortNumberFormat } from "@/utils/numbers";

import { Badge, Button, Popover, Stack, Text } from "../ui";

import GemIcon from "./gem-icon";
import GemRank from "./gem-rank";

const messages = defineMessages({
  dismissText: { id: "common.dismiss", defaultMessage: "Dismiss" },
  description: {
    id: "gem_badge.description",
    defaultMessage:
      "This is your total Truth Gems balance. You’ve earned it through your content contributions and engagement.",
  },
  gems: {
    id: "gem_badge.gems",
    defaultMessage: "{gems, plural, one {Gem} other {Gems}}",
  },
  leaderboardCta: {
    id: "gem_badge.leaderboard_cta",
    defaultMessage: "View Leaderboard",
  },
  truthGems: { id: "gem_badge.truth_gems", defaultMessage: "Truth Gems" },
  subscribeText: {
    id: "gem_badge.subscribe_text",
    defaultMessage:
      "Subscribe to Truth+ and earn rewards for your content and engagement.",
  },
  subscribe: { id: "gem_badge.subscribe", defaultMessage: "Subscribe" },
});

interface GemBadgeProps {
  isExpanded?: boolean;
}

export default function GemBadge({ isExpanded }: GemBadgeProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { currentAccount } = useAuth();

  const [isOpen, setIsOpen] = useState(false);

  if (!currentAccount || !currentAccount.features?.gems_visible) {
    return null;
  }

  const isGemsEnabled = currentAccount.features?.gems_enabled;

  const navigateToLeaderboard = () => {
    setIsOpen(false);
    navigate("/gems/leaderboard");
  };

  return (
    <Popover
      interaction="click"
      zIndex={1003}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      content={
        <Stack className="w-60 text-center">
          {isGemsEnabled ? (
            <Stack space={6}>
              <GemRank isCondensed />

              <Button
                block
                variant="outline"
                onClick={navigateToLeaderboard}
                className="py-0.75!"
              >
                {intl.formatMessage(messages.leaderboardCta)}
              </Button>
            </Stack>
          ) : (
            <>
              <div className="pointer-events-none absolute inset-0 z-0 bg-linear-to-bl from-primary-500/10 via-transparent to-transparent" />

              <Stack className="relative" space={1}>
                <Badge variant="default" className="absolute top-0 right-0">
                  <FormattedMessage id="common.new" defaultMessage="New" />
                </Badge>

                <Stack alignItems="center">
                  <GemIcon className="size-12" />

                  <Text size="xl" weight="bold">
                    {intl.formatMessage(messages.truthGems)}
                  </Text>
                </Stack>

                <Text size="sm" theme="muted" align="center">
                  {intl.formatMessage(messages.subscribeText)}
                </Text>
              </Stack>
            </>
          )}

          {!isGemsEnabled && (
            <Stack space={3} className="mt-4">
              <Button
                block
                variant="default"
                to="/settings/subscriptions"
                className="relative py-0.75!"
              >
                {intl.formatMessage(messages.subscribe)}
              </Button>

              <Button
                block
                variant="outline"
                onClick={navigateToLeaderboard}
                className="py-0.75!"
              >
                {intl.formatMessage(messages.leaderboardCta)}
              </Button>
            </Stack>
          )}
        </Stack>
      }
    >
      <button
        type="button"
        className={cn(
          "inline-flex shrink-0 items-center gap-x-1 rounded-full border px-3 py-2",
          {
            grayscale: !isGemsEnabled,
            "rounded-full! px-4! py-1.5! text-sm!": isExpanded,
          },
        )}
      >
        <GemIcon size={isExpanded ? 20 : 16} />

        <Text tag="span" size="xs" weight="bold">
          {shortNumberFormat(currentAccount.source.gems_count)}
        </Text>
      </button>
    </Popover>
  );
}
